import * as React from 'react';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useSignUp } from '@clerk/tanstack-react-start';
import {
  Stack,
  Input,
  Button,
  Text,
  Link,
  Separator,
  Box,
} from '@fragments-sdk/ui';
import { Beams } from '@/components/Beams';
import { stashDemoIntent } from '@/lib/demo-intent';
import s from './sign-up.module.scss';

export const Route = createFileRoute('/sign-up')({
  component: SignUpPage,
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
    next: typeof search.next === 'string' ? search.next : undefined,
    invite: typeof search.invite === 'string' ? search.invite : undefined,
  }),
});

function SignUpPage() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const navigate = useNavigate();
  const search = useSearch({ from: '/sign-up' });

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showVerification, setShowVerification] = React.useState(false);
  const postAuthUrl = search.invite
    ? `/invites?invite=${encodeURIComponent(search.invite)}`
    : search.next?.startsWith('/')
      ? search.next
      : '/';

  React.useEffect(() => {
    stashDemoIntent({ from: search.from, next: search.next });
  }, [search.from, search.next]);

  async function handleOAuth(strategy: 'oauth_google' | 'oauth_microsoft') {
    if (!isLoaded) return;
    try {
      await signUp.authenticateWithRedirect({
        strategy,
        redirectUrl: '/sso-callback',
        redirectUrlComplete: postAuthUrl,
      });
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'OAuth sign-up failed.');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;

    setError('');
    setLoading(true);

    try {
      await signUp.create({
        emailAddress: email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      await signUp.prepareEmailAddressVerification();

      if (
        signUp.status === 'missing_requirements' &&
        signUp.unverifiedFields.includes('email_address') &&
        signUp.missingFields.length === 0
      ) {
        setShowVerification(true);
      } else if (signUp.status === 'complete' && signUp.createdSessionId) {
        await setActive({ session: signUp.createdSessionId });
        navigate({ to: postAuthUrl });
      }
    } catch (err: unknown) {
      const clerkErr = err as {
        errors?: Array<{ longMessage?: string; message?: string }>;
      };
      const message =
        clerkErr.errors?.[0]?.longMessage ||
        clerkErr.errors?.[0]?.message ||
        'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;

    setError('');
    setLoading(true);

    try {
      await signUp.attemptEmailAddressVerification({ code });

      if (signUp.status === 'complete' && signUp.createdSessionId) {
        await setActive({ session: signUp.createdSessionId });
        navigate({ to: postAuthUrl });
      }
    } catch (err: unknown) {
      const clerkErr = err as {
        errors?: Array<{ longMessage?: string; message?: string }>;
      };
      const message =
        clerkErr.errors?.[0]?.longMessage ||
        clerkErr.errors?.[0]?.message ||
        'Invalid verification code. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={s.container}>
      {/* Left branded panel */}
      <div className={s.leftPanel}>
        <Beams className={s.beams} />
        <div className={s.leftContent}>
          <img
            src="/fragments-logo.png"
            alt="Fragments"
            width={48}
            height={48}
          />
          <Text
            size="2xl"
            weight="bold"
            style={{ color: '#fff', lineHeight: 1.3 }}
          >
            Ship AI&#8209;generated UI with confidence.
          </Text>
          <Text
            style={{
              color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.6,
            }}
          >
            Monitor design policy compliance, track violations, and enforce
            design system standards across your AI&#8209;generated interfaces.
          </Text>
        </div>
      </div>

      {/* Right form panel */}
      <div className={s.rightPanel}>
        <Box className={s.formWrapper}>
          <Stack gap="lg">
            <div className={s.mobileBranding}>
              <img
                src="/fragments-logo.png"
                alt="Fragments"
                width={36}
                height={36}
              />
            </div>

            <Stack gap="xs">
              <Text as="h1" size="xl" weight="bold">
                {showVerification ? 'Verify your email' : 'Create your account'}
              </Text>
              <Text color="secondary">
                {showVerification
                  ? `We sent a verification code to ${email}.`
                  : 'Get started with Fragments Cloud.'}
              </Text>
            </Stack>

            {showVerification ? (
              <>
                <form onSubmit={handleVerify}>
                  <Stack gap="md">
                    <Input
                      label="Verification code"
                      type="text"
                      placeholder="Enter the code from your email"
                      value={code}
                      onChange={setCode}
                      required
                    />
                    {error && (
                      <Text size="sm" color="primary">
                        {error}
                      </Text>
                    )}
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !isLoaded}
                    >
                      {loading ? 'Verifying\u2026' : 'Verify'}
                    </Button>
                  </Stack>
                </form>

                <Text
                  size="sm"
                  color="tertiary"
                  style={{ textAlign: 'center' }}
                >
                  Wrong email?{' '}
                  <Link
                    href="#"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowVerification(false);
                      setCode('');
                      setError('');
                    }}
                  >
                    Go back
                  </Link>
                </Text>
              </>
            ) : (
              <>
                <Stack gap="sm">
                  <Button
                    variant="outline"
                    onClick={() => handleOAuth('oauth_google')}
                    disabled={!isLoaded}
                    className={s.oauthBtn}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continue with Google
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleOAuth('oauth_microsoft')}
                    disabled={!isLoaded}
                    className={s.oauthBtn}
                  >
                    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden>
                      <path fill="#f35325" d="M1 1h10v10H1z" />
                      <path fill="#81bc06" d="M12 1h10v10H12z" />
                      <path fill="#05a6f0" d="M1 12h10v10H1z" />
                      <path fill="#ffba08" d="M12 12h10v10H12z" />
                    </svg>
                    Continue with Microsoft
                  </Button>
                </Stack>

                <Separator label="or" spacing="sm" />

                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <Input
                      label="First name"
                      type="text"
                      placeholder="Ada"
                      value={firstName}
                      onChange={setFirstName}
                      required
                    />
                    <Input
                      label="Last name"
                      type="text"
                      placeholder="Lovelace"
                      value={lastName}
                      onChange={setLastName}
                      required
                    />
                    <Input
                      label="Email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={setEmail}
                      required
                    />
                    <Input
                      label="Password"
                      type="password"
                      placeholder="Create a password"
                      value={password}
                      onChange={setPassword}
                      required
                    />
                    {error && (
                      <Text size="sm" color="primary">
                        {error}
                      </Text>
                    )}
                    <div id="clerk-captcha" />
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !isLoaded}
                    >
                      {loading ? 'Creating account\u2026' : 'Sign Up'}
                    </Button>
                  </Stack>
                </form>
              </>
            )}

            <Text size="sm" color="tertiary" style={{ textAlign: 'center' }}>
              Already have an account?{' '}
              <Link
                href={
                  search.invite
                    ? `/sign-in?invite=${encodeURIComponent(search.invite)}`
                    : '/sign-in'
                }
              >
                Sign in
              </Link>
            </Text>
          </Stack>
        </Box>
      </div>
    </div>
  );
}
